'use client';

import { traceChatEvent, traceChatState } from '@/lib/chat-trace';
import { fireHermesWarmup } from '@/lib/hermes-warmup';
import { readSseStream } from '@/lib/sse';
import { applyEvidenceFinal, hasAnyEvidence, ownViewRows } from '@/lib/turn-memory';
import type { TurnMemory } from '@/lib/turn-memory';
import { type FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import { MarkdownContent } from './MarkdownContent';

type ChatMessage = {
  role: 'user' | 'assistant';
  content: string;
  streaming?: boolean;
  /**
   * 2026-06-17 — wall-clock when this assistant slot was created.
   * Drives the ThinkingIndicator's elapsed-second counter and the
   * 30 s watchdog → 「响应超时，重试」 surface (Amy QA P1).
   */
  startedAt?: number;
  timedOut?: boolean;
  memory?: PublicMemory | null;
};

type PublicMemory = {
  concepts: Array<{ slug: string; display_name: string; origin?: string | null }>;
  /**
   * Stances the twin can cite whose parent concept did not rank this turn.
   *
   * Kept as its own list and rendered AFTER concepts, because Hermes numbers
   * `[Cn]` across concepts-then-direct. A drawer built from `concepts` alone
   * leaves a direct citation resolving to nothing — or worse, to whichever
   * concept happens to share the number.
   */
  direct_stances?: Array<{
    id: string;
    slug: string | null;
    position_md: string;
    authored_at?: string | null;
    origin?: string | null;
  }>;
  skills: Array<{ name: string; description: string }>;
  vault_passages: Array<{ title: string; publisher?: string | null; date?: string | null }>;
  external_sources: Array<{ name: string; provider?: string | null; date?: string | null }>;
};

/**
 * Anonymous public-facing chat for /u/[username]. Each POST creates a brand-
 * new conversation upstream — no client-side history concept in V2.1.
 * SSE stream pattern mirrors the authenticated ChatShell so the typing UX
 * stays consistent.
 */
export function PublicChat({
  username,
  displayName,
}: {
  username: string;
  displayName: string;
}) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Keeps this visitor's thread alive across turns. A ref, not state: it is read
  // inside the send handler and must never trigger a re-render or be captured
  // stale by a closure mid-stream. Signed and short-lived server-side, so it is
  // safe to hold in memory and pointless to tamper with. Deliberately NOT
  // persisted — a reload starting fresh is the honest behaviour, since the
  // twin's memory of the thread lives on the server for a bounded window.
  const conversationToken = useRef<string | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // 2026-06-17 — fire warmup on mount so the Container DO wakes up while
  // the visitor reads the profile header. Fail-soft; perf-only.
  // AXQA-20260618-04 (2026-06-18) — fireHermesWarmup dedupes within 45s
  // so re-mount (e.g. SPA navigation back to this profile) doesn't burn
  // a second ping on a container that's already warm.
  useEffect(() => {
    void fireHermesWarmup('/api/health/warmup');
  }, []);

  // AXQA-20260618-04 — re-fire warmup when the visitor focuses the
  // input box. Catches the case where they sat reading the profile for
  // > 60s (CF Containers' typical scale-down window): the mount-time
  // warmup expired, the container went back to sleep, but our 45s
  // helper TTL skips automatically if the page-mount ping is still
  // fresh — so this listener is cheap when not needed.
  const onInputFocus = useCallback(() => {
    void fireHermesWarmup('/api/health/warmup');
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll on every messages mutation including streaming deltas
  useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages]);

  const send = useCallback(
    async (text: string) => {
      setLoading(true);
      setError(null);
      let turnMemory: PublicMemory | null = null;
      // Round 115.1 #3 — once real grounding has arrived, a late/stray
      // `replace_meta` must not wipe the evidence drawer. Tracks whether any
      // memory_used / evidence_final carried content this turn.
      let sawEvidence = false;
      const userMsg: ChatMessage = { role: 'user', content: text };
      const assistantMsg: ChatMessage = {
        role: 'assistant',
        content: '',
        streaming: true,
        startedAt: Date.now(),
      };
      setMessages((prev) => [...prev, userMsg, assistantMsg]);

      try {
        const res = await fetch(`/api/public/u/${encodeURIComponent(username)}/chat`, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({
            content: text,
            ...(conversationToken.current ? { conversation_token: conversationToken.current } : {}),
          }),
        });
        if (!res.ok || !res.body) {
          // 2026-06-19 — owner daily cap exceeded → friendly toast.
          if (res.status === 429) {
            const txt = await res.text().catch(() => '');
            try {
              const j = JSON.parse(txt) as { hint?: string };
              throw new Error(j.hint || '当前分身今日已达用量上限，请稍后再试');
            } catch {
              throw new Error('当前分身今日已达用量上限，请稍后再试');
            }
          }
          throw new Error(`stream ${res.status}`);
        }

        await readSseStream(res.body, (ev) => {
          // Round 115 — QA-only trace so we can read the real transport's
          // event sequence in an in-app browser (Loop 9 evidence-drawer FAIL).
          traceChatEvent('public', ev as Record<string, unknown>);
          if (ev.type === 'message_id') {
            // The server re-issues this every turn; the newest one wins.
            if (typeof ev.conversation_token === 'string') {
              conversationToken.current = ev.conversation_token;
            }
          } else if (ev.type === 'chat_meta') {
            // AXQA-20260618-20/21 — emit SOUL version + Hermes build to
            // the browser console so QA can confirm public chat picks
            // up the latest persona save without DevTools Network panel.
            // 2026-06-19 — flat stringified line + structured event so
            // headless DOM scrapers can read hermes_build without
            // object expansion.
            if (typeof console !== 'undefined') {
              const m = ev as unknown as {
                soul_version?: number | null;
                tone_preset?: string | null;
                when_unsure?: string | null;
                hermes_build?: string | null;
                scope?: string | null;
              };
              console.warn(
                `[chat-meta] soul_version=${m.soul_version ?? 'null'} tone_preset=${m.tone_preset ?? 'null'} when_unsure=${m.when_unsure ?? 'null'} hermes_build=${m.hermes_build ?? 'null'} scope=${m.scope ?? 'null'}`,
              );
              console.warn('[chat-meta]', ev);
            }
          } else if (ev.type === 'memory_used') {
            const memoryEvent = ev as unknown as Partial<PublicMemory>;
            const memory: PublicMemory = {
              concepts: memoryEvent.concepts ?? [],
              // Absent on an older payload; `[]` so nothing downstream has to
              // tell "no direct stances" apart from "an orchestrator that
              // predates them".
              direct_stances: memoryEvent.direct_stances ?? [],
              skills: memoryEvent.skills ?? [],
              vault_passages: memoryEvent.vault_passages ?? [],
              external_sources: [],
            };
            if (
              memory.concepts.length +
                (memory.direct_stances?.length ?? 0) +
                memory.skills.length +
                memory.vault_passages.length >
              0
            ) {
              sawEvidence = true;
            }
            turnMemory = memory;
            setMessages((prev) => {
              const next = prev.slice();
              const last = next[next.length - 1];
              if (last?.role === 'assistant') next[next.length - 1] = { ...last, memory };
              return next;
            });
          } else if (ev.type === 'evidence_final') {
            const evidence = ev as unknown as Partial<PublicMemory>;
            // Uses the shared reducer, so the tests that pin this behaviour
            // drive the same code rather than a copy of it.
            turnMemory = applyEvidenceFinal(
              turnMemory as unknown as TurnMemory | null,
              evidence as Partial<TurnMemory>,
            ) as unknown as PublicMemory;
            if (hasAnyEvidence(turnMemory as unknown as TurnMemory)) {
              sawEvidence = true;
            }
            setMessages((prev) => {
              const next = prev.slice();
              const last = next[next.length - 1];
              if (last?.role === 'assistant') {
                next[next.length - 1] = { ...last, memory: turnMemory };
              }
              return next;
            });
          } else if (ev.type === 'tool_result' && typeof ev.name === 'string') {
            const toolName = ev.name;
            const result = ev.result as Record<string, unknown> | null | undefined;
            if (
              !toolName.startsWith('frameworks__') &&
              result &&
              typeof result === 'object' &&
              !('error' in result)
            ) {
              const provider =
                typeof result.source === 'string'
                  ? result.source
                  : typeof result.provider === 'string'
                    ? result.provider
                    : null;
              const date =
                typeof result.latest_observation_date === 'string'
                  ? result.latest_observation_date
                  : typeof result.retrieved_at_utc === 'string'
                    ? result.retrieved_at_utc
                    : null;
              const currentMemory = turnMemory ?? {
                concepts: [],
                skills: [],
                vault_passages: [],
                external_sources: [],
              };
              if (!currentMemory.external_sources.some((source) => source.name === toolName)) {
                turnMemory = {
                  ...currentMemory,
                  external_sources: [
                    ...currentMemory.external_sources,
                    { name: toolName, provider, date },
                  ],
                };
              }
              setMessages((prev) => {
                const next = prev.slice();
                const last = next[next.length - 1];
                if (last?.role !== 'assistant') return prev;
                const memory = last.memory ?? {
                  concepts: [],
                  skills: [],
                  vault_passages: [],
                  external_sources: [],
                };
                if (memory.external_sources.some((source) => source.name === toolName)) return prev;
                next[next.length - 1] = {
                  ...last,
                  memory: {
                    ...memory,
                    external_sources: [
                      ...memory.external_sources,
                      { name: toolName, provider, date },
                    ],
                  },
                };
                return next;
              });
            }
          } else if (ev.type === 'delta' && typeof ev.text === 'string') {
            const chunk = ev.text;
            setMessages((prev) => {
              const next = prev.slice();
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = { ...last, content: last.content + chunk };
              }
              return next;
            });
          } else if (ev.type === 'replace_content' && typeof ev.text === 'string') {
            setMessages((prev) => {
              const next = prev.slice();
              const last = next[next.length - 1];
              if (last?.role === 'assistant') {
                next[next.length - 1] = { ...last, content: ev.text as string };
              }
              return next;
            });
          } else if (ev.type === 'replace_meta') {
            // Round 115.1 #3 — a boundary-reply `replace_meta` clears the
            // evidence chip, but it must not wipe a drawer that real grounding
            // already populated this turn (guards against event misordering /
            // a late replace_meta racing evidence_final).
            if (sawEvidence) {
              traceChatState('public', 'replace_meta-ignored', { reason: 'evidence-present' });
            } else {
              turnMemory = null;
              setMessages((prev) => {
                const next = prev.slice();
                const last = next[next.length - 1];
                if (last?.role === 'assistant') last.memory = null;
                return next;
              });
            }
          } else if (ev.type === 'done') {
            setMessages((prev) => {
              const next = prev.slice();
              const last = next[next.length - 1];
              if (last && last.role === 'assistant') {
                next[next.length - 1] = { ...last, streaming: false, memory: turnMemory };
              }
              return next;
            });
          }
        });

        setMessages((prev) => {
          const next = prev.slice();
          const last = next[next.length - 1];
          if (last && last.role === 'assistant') {
            // `evidence_final` is emitted after Hermes' `done` marker. React
            // may batch both event updates, so make the post-stream merge the
            // authoritative fallback instead of only toggling `streaming`.
            const mergedMemory = turnMemory ?? last.memory;
            next[next.length - 1] = {
              ...last,
              streaming: false,
              memory: mergedMemory,
            };
            traceChatState('public', 'stream-finalized', {
              content_len: last.content.length,
              has_memory: Boolean(mergedMemory),
              concepts: mergedMemory?.concepts.length ?? 0,
              skills: mergedMemory?.skills.length ?? 0,
              vault: mergedMemory?.vault_passages.length ?? 0,
              external: mergedMemory?.external_sources.length ?? 0,
            });
          }
          return next;
        });
        setInput('');
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Streaming failed.');
        setMessages((prev) => {
          const next = prev.slice();
          const last = next[next.length - 1];
          if (last && last.role === 'assistant') {
            next[next.length - 1] = {
              ...last,
              streaming: false,
              content: last.content || '⚠ Failed to load response.',
            };
          }
          return next;
        });
      } finally {
        setLoading(false);
      }
    },
    [username],
  );

  const onSubmit = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      const text = input.trim();
      if (!text || loading) return;
      send(text);
    },
    [input, loading, send],
  );

  return (
    <div
      // V2.11 (2026-05-21) — same height-fit logic as TwinChatLp.
      className="grid grid-rows-[1fr_auto] rounded-2xl border border-soft bg-white h-[calc(100dvh-260px-env(safe-area-inset-bottom))] md:h-[calc(100dvh-200px)] min-h-[360px]"
    >
      <div ref={scrollRef} className="px-5 py-5 overflow-y-auto text-base">
        {messages.length === 0 && (
          <p className="text-dusk text-sm m-0">
            Ask {displayName} anything. Responses carry confidence tags (
            <code className="font-mono text-xs">实时</code> /{' '}
            <code className="font-mono text-xs">历史</code> /{' '}
            <code className="font-mono text-xs">基于推断</code>) — this is an AI representation, not
            financial advice.
          </p>
        )}
        <div className="mt-4 flex flex-col gap-3">
          {messages.map((m, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: append-only
              key={i}
              className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div className="max-w-[85%] min-w-0">
                <div
                  className={`px-4 py-3 leading-relaxed whitespace-pre-wrap break-words rounded-2xl ${
                    m.role === 'user'
                      ? 'bg-warmth-dark text-white rounded-br-sm'
                      : 'bg-cream border border-soft text-ink rounded-bl-sm'
                  }`}
                >
                  {/*
                  2026-06-17 — pre-first-token "正在思考…(N 秒)" indicator,
                  parity with TwinChatLp's ChatShell. Amy QA P1: the prior
                  `…` glyph gave no signal that work was happening, leading
                  visitors to mistake a 12-30 s cold start for a hang.
                */}
                  {m.streaming && m.content === '' ? (
                    <ThinkingIndicator startedAt={m.startedAt ?? Date.now()} />
                  ) : (
                    <>
                      {m.role === 'assistant' ? (
                        <MarkdownContent
                          content={m.content}
                          className="text-base leading-relaxed"
                        />
                      ) : (
                        m.content
                      )}
                      {m.streaming && (
                        <span aria-label="正在输入" className="text-dusk ml-1">
                          …
                        </span>
                      )}
                    </>
                  )}
                </div>
                {/*
                  Round 115 — render the evidence strip as soon as memory is
                  populated, not only after `streaming` flips false. Loop 9
                  saw the drawer stay hidden in a real browser when the
                  finalize/`done` state update raced; a populated strip should
                  never depend on that toggle. `PublicEvidenceStrip` returns
                  null while the memory total is 0, so this stays invisible
                  until real evidence arrives.
                */}
                {m.role === 'assistant' && m.content !== '' && m.memory && (
                  <PublicEvidenceStrip memory={m.memory} />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={onSubmit} className="border-t border-soft px-4 py-3 bg-white rounded-b-2xl">
        {error && (
          <p role="alert" className="text-warmth text-sm m-0 mb-2">
            {error}
          </p>
        )}
        <div className="flex items-end gap-2">
          <label htmlFor="public-chat-input" className="sr-only">
            向 {displayName} 提问
          </label>
          <textarea
            id="public-chat-input"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onFocus={onInputFocus}
            placeholder={`问 ${displayName}…`}
            disabled={loading}
            rows={1}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) {
                e.preventDefault();
                const text = input.trim();
                if (text && !loading) send(text);
              }
            }}
            className="flex-1 px-4 py-2 rounded-full border border-soft bg-cream/40 text-base text-ink placeholder:text-dusk/60 focus:outline-none focus:ring-2 focus:ring-warmth/40 focus:border-warmth/40 transition resize-none disabled:opacity-60"
            style={{ minHeight: 36, maxHeight: 120, lineHeight: '1.4' }}
          />
          <button
            type="submit"
            disabled={loading || !input.trim()}
            className="shrink-0 px-4 min-h-[36px] rounded-full bg-warmth-dark text-white font-medium text-sm whitespace-nowrap hover:bg-warmth transition disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {loading ? '…' : '发送'}
          </button>
        </div>
      </form>
    </div>
  );
}

function PublicEvidenceStrip({ memory }: { memory: PublicMemory }) {
  // Own views = concepts + direct stances, in ONE citation sequence. Counting
  // and rendering concepts alone made a direct-only turn show no evidence strip
  // at all: the answer carried [C1] and the visitor had nothing to open
  // (@QA-CEA, real PublicChat behaviour test).
  const ownViews = ownViewRows(memory);
  if (!hasAnyEvidence({ ...memory, direct_stances: memory.direct_stances ?? [] })) return null;
  return (
    <details className="mt-1.5 text-left">
      <summary className="inline-flex min-h-9 cursor-pointer select-none items-center gap-1 rounded-full border border-soft bg-white px-3 py-1.5 text-xs text-dusk hover:text-ink">
        <span>证据</span>
        <span>
          {ownViews.length} 观点 · {memory.skills.length} 框架 · {memory.vault_passages.length} 原文
          · {memory.external_sources.length} 外部数据
        </span>
      </summary>
      <div className="mt-1.5 border-l-2 border-soft pl-3 text-xs leading-relaxed text-dusk">
        {ownViews.map((row) => (
          <p key={row.key} className="m-0" data-cite={row.cite} data-stance-id={row.stanceId}>
            [{row.cite}] 本人观点 · {row.label}
          </p>
        ))}
        {memory.skills.map((skill) => (
          <p key={`skill-${skill.name}`} className="m-0">
            思维框架 · {skill.name}
          </p>
        ))}
        {memory.vault_passages.map((passage, index) => (
          <p key={`passage-${passage.title}-${index}`} className="m-0">
            原文 · {passage.title}
            {passage.date ? ` · ${passage.date.slice(0, 10)}` : ''}
          </p>
        ))}
        {memory.external_sources.map((source) => (
          <p key={`tool-${source.name}`} className="m-0">
            外部数据 · {source.name.replaceAll('__', ' · ')}
            {source.provider ? ` · ${source.provider}` : ''}
            {source.date ? ` · ${source.date.slice(0, 10)}` : ''}
          </p>
        ))}
      </div>
    </details>
  );
}

/**
 * 2026-06-17 — pre-first-token wait state. Counts up from startedAt,
 * mirrors TwinChatLp's authenticated variant. After 30 s, switches to a
 * "响应较慢，可重新发送" copy so users know it isn't silently hung —
 * the Streaming retry itself is one click away (resend the last text).
 */
function ThinkingIndicator({ startedAt }: { startedAt: number }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);
  const elapsedSec = Math.max(0, Math.floor((now - startedAt) / 1000));
  const slow = elapsedSec >= 30;
  return (
    <span className="inline-flex items-center gap-2 text-dusk text-[15px] italic">
      <span aria-hidden className="inline-flex gap-1">
        <span className="w-1.5 h-1.5 rounded-full bg-dusk/60 animate-bounce [animation-delay:-0.3s]" />
        <span className="w-1.5 h-1.5 rounded-full bg-dusk/60 animate-bounce [animation-delay:-0.15s]" />
        <span className="w-1.5 h-1.5 rounded-full bg-dusk/60 animate-bounce" />
      </span>
      <span>{slow ? '响应较慢，可稍后重试…' : '正在思考…'}</span>
      <span className="text-text-mute text-[13px] not-italic tabular-nums">({elapsedSec} 秒)</span>
    </span>
  );
}
